import { useNotify } from '@globaleris/ui.library'
import { Box, Chip, Switch } from '@material-ui/core'
import SearchIcon from '@material-ui/icons/Search'
import moment from 'moment'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import { TaskModel } from '../../api/Argentia/endpoints/tasks/model/tasks'
import { EnumEstadoTarea } from '../../api/oData/enums/enumEstadoTarea'
import Page from '../../components/page/page'
import PageCenterColumn from '../../components/page/pageCenterColumn'
import PageHeader from '../../components/page/pageHeader'
import DateFilter from '../../components/shared/dateFilter/dateFilter'
import TabsWithPermissions, { Tab } from '../../components/tabsWithPermissions/tabsWithPermissions'
import { enumToMapValues, MapToArray } from '../../helpers'
import { useHttpError, usePageState } from '../../hooks'
import { DateFilters, DatePeriod, NonAuthRoutes } from '../../model'
import {
  getActiveUser,
  getTasksSearchFilterState,
  RootStoreModel,
  storeActiveTabForCurrentPage,
  storeDateFilterForCurrentPage,
  storeShowFilterCardState,
  storeShowTasksState,
  storeTasksDataTable,
  storeTasksSearchFilterState
} from '../../redux'
import {
  SearchFilterCardTasks,
  TaskFormSearchFilter
} from './components/searchFilterCardTasks/searchFilterCardTasks'
import { TasksTableCard } from './components/tasksTableCard/tasksTableCard'
import {
  mapResponseDataToTaskTableModel,
  mapTaskFilterRequest,
  mapTaskFormSearchToFilterRequest
} from './tasks.mapper'
import { TaskPageTabs } from './tasks.model'
import { fetchTasksExpanded, fetchTasksFilteredExpanded } from './tasks.services'

interface TasksPageProps {
  location?: {
    state: {
      forceRefreshFlag?: boolean
    }
  }
}

export const TasksPage: React.FC<TasksPageProps> = props => {
  const [t] = useTranslation(['globalerisLibrary', 'tasks', 'enums'])
  const history = useHistory()
  const { dataTableFilter, dataTableSearch } = usePageState()
  const { errorProps, showError, clearError } = useHttpError()
  const { info, error } = useNotify()
  const { activeTab, dateFilter, customFilter } = usePageState()

  // Date Filter
  const [datePeriod, setDatePeriod] = useState<DatePeriod>({
    start: customFilter?.startDate
      ? customFilter.startDate
      : dateFilter
        ? moment(dateFilter.initDate).toDate()
        : moment().toDate(),
    end: customFilter?.endDate
      ? customFilter.endDate
      : dateFilter
        ? moment(dateFilter.endDate).toDate()
        : moment().toDate(),
    dateFilter: dateFilter?.activeDateFilter ?? DateFilters.RECORDS_LIM_200,
    customFilter: customFilter ?? undefined
  })

  // Tabs Management
  const [currentTab, setCurrentTab] = useState<number>(activeTab ?? 0)
  const currentTabRef = useRef(currentTab)

  // Tasks info
  const tasks = useSelector((state: RootStoreModel) => state.tasksState.tasksDataTable)
  const [tableTitle, setTableTitle] = useState<string>()
  const [isFetchingTasksTable, setIsFetchingTasksTable] = useState<boolean>(false)
  const showTasks = useSelector((state: RootStoreModel) => state.tasksState.showTasks)
  const showSearchFilter = useSelector((state: RootStoreModel) => state.tasksState.showSearchFilter)
  const storedFilters = getTasksSearchFilterState()
  const forceRefreshFlag = props.location?.state?.forceRefreshFlag

  // Tasks KPIs
  const [isFetchingAnnouncement, setIsFetchingAnnouncement] = useState<boolean>(false)
  const [isFetchingKpis, setIsFetchingKpis] = useState<boolean>(false)

  // My Tasks Filter
  const activeUser = getActiveUser()?.name
  const [myTasksFilterIsActive, setMyTasksFilterIsActive] = useState<boolean>(false)
  const filteredTasks = myTasksFilterIsActive
    ? tasks.filter(task => task.assignedUser === activeUser)
    : tasks

  // #region Handlers Starts

  // ------------------- Date Filter Handlers -----------------------------//
  const handleDateFilterChange = async (
    startDate: Date,
    endDate: Date,
    activeDateFilter?: DateFilters
  ): Promise<void> => {
    storeShowFilterCardState(false)
    storeTasksSearchFilterState({} as TaskFormSearchFilter)
    setDatePeriod({ start: startDate, end: endDate, dateFilter: activeDateFilter! })

    const tabId = filteredTabsArray[currentTabRef.current]?.id ?? 1

    const request = mapTaskFilterRequest(startDate, endDate, activeDateFilter, tabId)
    getTasksFilteredExpanded(request)
    storeDateFilterForCurrentPage({
      activeDateFilter: activeDateFilter,
      initDate: startDate,
      endDate: endDate
    })
  }

  // ------------------- Filter My Tasks Handlers -----------------------------//
  const handleFilterMyTasksClick = (e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setMyTasksFilterIsActive(checked)
  }

  // ------------------- SearchFilter Handlers -----------------------------//

  const onSearchCardHandler = () => {
    showTasks && storeShowTasksState(false)
    !showSearchFilter && storeShowFilterCardState(true)
    storeTasksDataTable([])
    setTableTitle(t('tasks:components.taskTableCard.table.title.search'))
  }

  const onSearchFilterHandler = (formState: TaskFormSearchFilter) => {
    storeShowTasksState(true)
    storeTasksSearchFilterState(formState)
    setDatePeriod({} as DatePeriod)

    const tabId = filteredTabsArray[currentTabRef.current]?.id ?? 1

    const request = mapTaskFormSearchToFilterRequest(
      formState.startDate,
      formState.endDate,
      formState.taskType,
      formState.criticality,
      tabId
    )
    getTasksFilteredExpanded(request)
  }

  // clean filter is inside the component

  const onCancelSearchHandler = () => {
    showSearchFilter && storeShowFilterCardState(false)
    getTasksExpanded()
    setTableTitle(t('tasks:components.taskTableCard.table.title.latest'))
  }

  // ------------------- LatestCLick Handlers -----------------------------//

  const onLatestClickHandler = () => {
    storeShowTasksState(true)
    showSearchFilter && storeShowFilterCardState(false)

    setTableTitle(t('tasks:components.taskTableCard.table.title.latest'))
    getTasksExpanded()
  }

  // ------------------- Add Task Handlers -----------------------------//

  const handleAddTask = () => {
    history.push(NonAuthRoutes.TasksAdd)
  }

  // ------------------- DataTable Actions Handlers -----------------------------//

  const handleEditTask = (selectedRow: TaskModel) => {
    history.push(NonAuthRoutes.TasksEdit, {
      task: {
        ...selectedRow,
        closingDate: selectedRow.closingDate
          ? moment(selectedRow.closingDate, 'DD-MM-YYYY HH:mm')
              .parseZone()
              .startOf('day')
              .format('YYYY-MM-DD')
          : undefined,
        creationDate: moment(selectedRow.creationDate, 'DD-MM-YYYY HH:mm')
          .parseZone()
          .startOf('day')
          .format('YYYY-MM-DD')
      },
      taskDomain: selectedRow.taskType.domain
    })
  }

  const onTabChangeHandler = (tabIdx: number) => {
    if (currentTab !== tabIdx) {
      setCurrentTab(tabIdx)
      storeActiveTabForCurrentPage(tabIdx)
    }

    const tabId = filteredTabsArray[tabIdx]?.id!

    const request = !showSearchFilter
      ? mapTaskFilterRequest(datePeriod.start, datePeriod.end, datePeriod.dateFilter, tabId)
      : mapTaskFormSearchToFilterRequest(
          storedFilters.startDate,
          storedFilters.endDate,
          storedFilters.taskType,
          storedFilters.criticality,
          tabId
        )

    getTasksFilteredExpanded(request)
  }
  // #endregion Handlers Ends

  // #region Tab Config Starts
  const tabsArray = MapToArray(enumToMapValues(TaskPageTabs))
  const tabsDefinition: Map<number, Tab> = new Map(
    tabsArray.map((tab, tabIndex) => [
      tabIndex,
      {
        id: tab.value,
        label: t(`tasks:tabs.${tab.name}`),
        hasPermission: true
      }
    ])
  )
  const filteredTabsArray = Array.from(tabsDefinition.values()).filter(tab => tab.hasPermission)

  // #endregion Tab Config Ends

  // #region DateFilter config starts

  let filters = [
    {
      filter: DateFilters.TODAY,
      label:
        filteredTabsArray[currentTab].id === TaskPageTabs.FINALIZADA
          ? t('translation:components.dateFilter.todayNoGasClosed')
          : t('translation:components.dateFilter.todayNoGasOpen')
    },
    {
      filter: DateFilters.LASTWEEK,
      label:
        filteredTabsArray[currentTab].id === TaskPageTabs.FINALIZADA
          ? t('translation:components.dateFilter.lastWeekClosed')
          : t('translation:components.dateFilter.lastWeekOpen')
    },
    {
      filter: DateFilters.RECORDS_LIM_200,
      label: t('translation:components.dateFilter.recordsLim200')
    }
  ]

  // #endregion DateFilter config ends

  // #region Functions Starts
  // #endregion Functions Ends

  // #region Form Definition Starts
  // #endregion Form Definition Ends

  // #region DataTable Config Starts
  // #endregion DataTable Config Ends

  // #region Hooks Starts

  const getTasksExpanded = useCallback(async () => {
    setIsFetchingTasksTable(true)
    clearError()
    await fetchTasksExpanded()
      .then(response => {
        if (response.success && response.data) {
          storeTasksDataTable(mapResponseDataToTaskTableModel(response.data))
        } else {
          showError({
            errorCode: response.error?.code,
            onRefresh: () => getTasksExpanded(),
            title: response.error?.message
          })
        }
      })
      .finally(() => {
        setIsFetchingTasksTable(false)
      })
  }, [])

  const getTasksFilteredExpanded = useCallback(
    async request => {
      setIsFetchingTasksTable(true)
      clearError()
      await fetchTasksFilteredExpanded(request)
        .then(response => {
          if (response.success && response.data) {
            storeTasksDataTable(mapResponseDataToTaskTableModel(response.data))
          } else {
            showError({
              errorCode: response.error?.code,
              onRefresh: () => fetchTasksFilteredExpanded(request),
              title: response.error?.message
            })
          }
        })
        .finally(() => {
          setIsFetchingTasksTable(false)
        })
    },
    [currentTab]
  )

  useEffect(() => {
    if (forceRefreshFlag) {
      storeShowTasksState(false)
    }
  }, [forceRefreshFlag])

  useEffect(() => {
    if (activeTab !== undefined) {
      setCurrentTab(activeTab)
    } else {
      storeActiveTabForCurrentPage(0)
    }
  }, [activeTab])

  useEffect(() => {
    currentTabRef.current = currentTab
  }, [currentTab])

  // #endregion Hooks Ends

  // #region Render Functions Starts

  const renderTabContent = (activeTab: number) => {
    return (
      <>
        <SearchFilterCardTasks
          selectedTaskState={filteredTabsArray[activeTab]?.id! as EnumEstadoTarea}
          showCard={showSearchFilter}
          filters={storedFilters}
          onSearch={onSearchFilterHandler}
          onCancel={onCancelSearchHandler}
        />
        <TasksTableCard
          show
          title={tableTitle}
          isLoading={isFetchingTasksTable}
          data={filteredTasks}
          onTableEditClick={handleEditTask}
          onTableAddClick={handleAddTask}
          dataTableFilter={dataTableFilter}
          dataTableSearch={dataTableSearch}
          dataTableErrorProps={errorProps}
        />
      </>
    )
  }

  // #endregion Render Functions Ends

  return (
    <Page fullWidth={true}>
      <PageHeader title={t('tasks:landingPage.title')}>
        <Box display="flex" flexDirection="column" alignItems="flex-end">
          <Box display="flex" justifyContent="flex-end">
            <DateFilter
              inactive={customFilter || showSearchFilter}
              enabled={!isFetchingTasksTable}
              initialFilter={
                dateFilter === undefined ||
                dateFilter?.activeDateFilter === null ||
                dateFilter?.activeDateFilter === undefined
                  ? DateFilters.RECORDS_LIM_200
                  : dateFilter.activeDateFilter
              }
              // controlledActiveFilter={controlledActiveFilter}
              onSelectDate={handleDateFilterChange}
              filters={filters}
            />
            <Chip
              size="small"
              variant={!isFetchingTasksTable ? 'default' : 'outlined'}
              icon={<SearchIcon></SearchIcon>}
              clickable={!isFetchingTasksTable}
              style={{
                marginRight: 16,
                color: isFetchingTasksTable ? '#999' : undefined
              }}
              label={`${t('translation:components.searchFilterV2.chipLabel')}`}
              color={!showSearchFilter ? 'default' : 'primary'}
              onClick={() => storeShowFilterCardState(true)}
            />
          </Box>
          <Box display="flex" justifyContent="flex-end" alignItems="start">
            <Switch
              checked={myTasksFilterIsActive}
              onChange={handleFilterMyTasksClick}
              color="primary"
              size="small"
            />
            <Box
              display="flex"
              style={{
                marginRight: 16,
                marginTop: 3
              }}
            >
              {t('tasks:landingPage.header.filterMyTasks')}
            </Box>
          </Box>
        </Box>
      </PageHeader>
      <PageCenterColumn>
        <TabsWithPermissions
          tabs={tabsDefinition}
          tabDefault={currentTab}
          onTabChange={onTabChangeHandler}
        ></TabsWithPermissions>

        {renderTabContent(activeTab ? activeTab : currentTab)}
        {/* <KpiCardTasks
          onSearchClickHandler={onSearchCardHandler}
          onLatestClickHandler={onLatestClickHandler}
          onAddTask={handleAddTask}
        /> */}
      </PageCenterColumn>
    </Page>
  )
}
